import React, { useState, useEffect } from "react";
import {
  Container, Typography, Paper, Box, TextField, Button, Grid,
  FormControl, Select, MenuItem, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  getTenants,
  addTenantData,
  updateTenantData,
  deleteTenantData
} from "../db/tenantApi";

const TenantManagement = () => {

  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    companyName: "",
    appAdminName: "",
    appAdminEmail: "",
    tenantType: "",
    status: "active"
  });

  const [hosts, setHosts] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);

  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    loadHosts();
  }, []);

  const loadHosts = async () => {
    const data = await getTenants();
    setHosts(data);
  };

  if (!isSuperAdmin()) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedHost) {
      await updateTenantData(selectedHost.id, formData);
    } else {
      await addTenantData(formData, user.uid);
    }

    setFormData({
      companyName: "",
      appAdminName: "",
      appAdminEmail: "",
      tenantType: "",
      status: "active"
    });

    setSelectedHost(null);
    setSubmitSuccess(true);
    loadHosts();
  };

  const handleEdit = (tenant) => {
    setSelectedHost(tenant);
    setFormData({
      companyName: tenant.tenantName,
      appAdminName: tenant.appAdminName,
      appAdminEmail: tenant.appAdminEmail,
      tenantType: tenant.tenantType
    });
  };

  const handleDelete = async (id) => {
    await deleteTenantData(id);
    loadHosts();
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>

      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: "2rem",
            fontWeight: 900,
            fontFamily: "'Unbounded', sans-serif",
            color: "#1c1400"
          }}>
            Organization Management
          </Typography>

          <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
            Manage Orgs and assign administrators
          </Typography>
        </Box>

        {/* FORM */}
        <Paper sx={{
          p: 4,
          borderRadius: "16px",
          border: "2px solid #f0d060",
          background: "#fff"
        }}>
          <Typography sx={{ fontWeight: 700, mb: 3 }}>
            {selectedHost ? "Edit Org" : "Add New Org"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Admin Name"
                name="appAdminName"
                value={formData.appAdminName}
                onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Admin Email"
                name="appAdminEmail"
                value={formData.appAdminEmail}
                onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Organization Type</InputLabel>
                <Select
                  name="tenantType"
                  value={formData.tenantType}
                  onChange={handleChange}
                >
                  <MenuItem value={100}>Residential</MenuItem>
                  <MenuItem value={200}>Commercial</MenuItem>
                  <MenuItem value={300}>Mixed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              sx={{
                background: "#1c1400",
                color: "#fde047",
                "&:hover": {
                  background: "#f59e0b",
                  color: "#1c1400"
                }
              }}
              onClick={handleSubmit}
            >
              {selectedHost ? "Update Org" : "Add Organization"}
            </Button>
          </Box>
        </Paper>

        {/* TABLE */}
        <Paper sx={{
          mt: 4,
          borderRadius: "16px",
          border: "2px solid #f0d060"
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ background: "#fef3c7" }}>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {hosts.map((host) => (
                  <TableRow key={host.id}>
                    <TableCell>{host.tenantName}</TableCell>
                    <TableCell>
                      {host.tenantType === 100 ? "Residential"
                        : host.tenantType === 200 ? "Commercial" : "Mixed"}
                    </TableCell>
                    <TableCell>{host.appAdminName}</TableCell>
                    <TableCell>{host.appAdminEmail}</TableCell>

                    <TableCell>
                      <IconButton onClick={() => handleEdit(host)}>
                        <Edit sx={{ color: "#f59e0b" }} />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(host.id)}>
                        <Delete sx={{ color: "#dc2626" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      </Container>
    </Box>
  );
};

export default TenantManagement;